// eslint-disable-next-line import/no-unresolved
import KeenSlider from 'https://cdn.skypack.dev/pin/keen-slider@v6.4.1-2bW5h7N5TLEKpP3hb61d/mode=imports,min/optimized/keen-slider.js' // https://keen-slider.io/docs
// import { getElements } from '@bao/utils.js'

/**
 * Global settings
 * @param {number} minSlides             - Determines if the slider initialises if passing the number
 *                                         given. Leave blank if always running carousel.
 * Size specific settings
 * @param {Boolean} carouselDisabled     - If this option is set to true, the slider is disabled. No styles
 *                                         or events will be applied. The default is false.
 * @param {Boolean} autoplay             - Enables or disables autoplay rotate.
 * @param {Boolean} drag                 - Enables or disables mouse and touch control. Default is false.
 * @param {number} initial               - Sets the index of the initially visible slide. Default is 0.
 * @param {Boolean} loop                 - Enables or disables carousel/loop functionality of the slider.
 *                                         Default is false.
 * @param {String} mode                  - 'snap' | 'free' | 'free-snap' - Sets the animation that is
 *                                         applied after a drag ends. Default is 'snap'.
 * @param {Boolean} rubberband           - Enables or disables the rubberband behavior for dragging and
 *                                         animation after a drag. Default is false.
 * @param {number} slidesPerView         - Determines what size the slides should be in relation to
 *                                         the viewport/container. Default 1, tablet 2, desktop 4.
 * @param {number} slidesSpacing         - Defines the spacing between slides in pixel. Default is 0.
 * @param {String / number} SlidesOrigin - 'auto' | 'center' | number - Sets the origin of the slides.
 *                                         Default is 'auto.
 *
 * all options have tablet- and desktop- options for appropriate options.
 *
 */

class GlobalCarousel extends window.BAO.CustomElement() {
  /*
  constructor () {
    super()
    this.initialise()

    if (!Shopify.designMode) return

    this.sectionLoad = this.sectionLoad.bind(this)
    this.sectionUnload = this.sectionUnload.bind(this)
    this.showBlock = this.showBlock.bind(this)

    this.listeners.add(document, 'shopify:section:load', this.sectionLoad)
    this.listeners.add(document, 'shopify:section:unload', this.sectionUnload)
    this.listeners.add(document, 'shopify:section:select', this.sectionLoad)
    this.listeners.add(document, 'shopify:section:deselect', this.sectionLoad)

    this.listeners.add(document, 'shopify:block:select', this.showBlock)
  }
  */

  reinitialiseTrack () {
    if (!this._hasBeenInitialised) return
    this.carousel.track.init()
  }

  connectedCallback () {
    if (!this.els?.slides || !this.els?.slide) return
    if (this._hasBeenInitialised) return
    this._hasBeenInitialised = true
    this.setupListeners()
    this.initialise()
  }

  handleAutoplay (run, instance) {
    clearInterval(this.interval)
    this.interval = setInterval(() => {
      if (run && instance) {
        instance.next()
      }
    }, 5000)
  }

  set carousel (carousel) {
    this._carousel = carousel
    this.setProgressBarWidth()
    this.setProgressPercentage()
  }

  get carousel () {
    return this._carousel
  }

  setupListeners () {
    super.setupListeners()
    this.setProgressListener()
    this.setSlideListener()
    this.setButtonListener()
  }

  /*
  sectionLoad () {
    this.initialise()
    this.connectedCallback()
  }

  sectionUnload () {
    this.disconnectedCallback()
  }

  showBlock (event) {
    const blockId = event?.detail?.blockId
    const shopifyBlockSelector = `data-shopify-editor-block`
    const blockEls = getElements(`[${shopifyBlockSelector}]`, { context: this })
    const currentBlockEl = blockEls.find(blockEl => {
      const blockJSON = blockEl.getAttribute(shopifyBlockSelector)
      const blockData = blockJSON ? JSON.parse(blockJSON) : null
      return blockData?.id.toString() === blockId.toString()
    })

    const slideEl = currentBlockEl
      ? currentBlockEl.closest(this.els?.slide?.selector)
      : null
    const index = slideEl ? slideEl?.dataset?.slide : null

    if (!index) return

    this.carousel.moveToIdx(parseInt(index), 10)
  }
  */

  initialise () {
    if (this.sliderInitStart() === false) return
    this.carousel = this.initialiseCarousel()
    this.currentIndex = 0
  }

  initialiseCarousel () {
    return new KeenSlider(this.slidesEl, {
      selector: this.els.slide.selector,
      disabled: this.hasAttribute('data-carousel-disabled'),
      drag: this.hasAttribute('data-drag') || false,
      initial: this.getDataValue('initial') || 0,
      loop: this.hasAttribute('data-loop') || false,
      mode: this.getDataValue('mode') || 'snap',
      rubberband: this.hasAttribute('data-rubberband') || false,
      slides: {
        origin: this.getDataValue('slidesOrigin') || 'auto',
        perView: this.getDataValue('slidesPerView') || 1,
        spacing: this.getDataValue('slidesSpacing') || 0,
      },
      breakpoints: {
        [`(min-width: ${this.tabletBreakpoint}px)`]: this
          .tabletBreakpointOptions,
        [`(min-width: ${this.desktopBreakpoint}px)`]: this
          .desktopBreakpointOptions,
      },
      created: instance => {
        this.setAttribute('data-initialized', 'true')

        if (this.autoplay) {
          this.slidesEl.addEventListener('mouseover', () => {
            this.handleAutoplay(false, instance)
          })
          this.slidesEl.addEventListener('mouseout', () => {
            this.handleAutoplay(this.autoplay, instance)
          })
          this.handleAutoplay(this.autoplay, instance)
        }
      },
      optionsChanged: instance => {
        this.setActiveSlide(instance)
        this.setProgressBarWidth()
        this.setProgress(instance)
        this.setProgressPercentage(instance)

        this.dispatchEvent(
          new CustomEvent('OPTIONS_CHANGED', { detail: instance }),
        )
      },
      slideChanged: instance => {
        this.setCarouselState(instance)
        this.setActiveSlide(instance)
        this.setProgress(instance)
        this.setProgressPercentage(instance)

        this.dispatchEvent(
          new CustomEvent('SLIDE_CHANGED', { detail: instance }),
        )
      },
      destroyed: () => {
        this.setAttribute('data-initialized', 'false')
      },
    })
  }

  /* Setup Options */

  getDataValue (option) {
    return this.dataset[option]
  }

  sliderInitStart () {
    if (!this.getDataValue('minSlides')) return true
    return this.slideEls.length <= parseInt(this.getDataValue('minSlides'))
  }

  get slidesEl () {
    return this.els?.slides?.element
  }

  get slideEls () {
    return this.els?.slide?.elements || []
  }

  get slideCount () {
    return this.els?.slide?.elements?.length || 1
  }

  get autoplay () {
    if (Shopify.designMode) return
    return this.hasAttribute('data-autoplay')
  }

  /* Tablet Options */
  get tabletBreakpoint () {
    return 768
  }

  get tabletBreakpointOptions () {
    const tabletOptions = {
      disabled:
        this.hasAttribute('data-tablet-carousel-disabled') ||
        this.hasAttribute('data-carousel-disabled') ||
        false,
      drag:
        this.hasAttribute('data-tablet-drag') ||
        this.hasAttribute('data-drag') ||
        true,
      initial:
        this.getDataValue('tabletInitial') || this.getDataValue('initial') || 0,
      loop:
        this.hasAttribute('data-tablet-loop') ||
        this.hasAttribute('data-loop') ||
        false,
      mode:
        this.getDataValue('tabletMode') || this.getDataValue('mode') || 'snap',
      rubberband:
        this.hasAttribute('data-tablet-rubberband') ||
        this.hasAttribute('data-rubberband') ||
        false,
      slides: {
        origin:
          this.getDataValue('tabletSlidesOrigin') ||
          this.getDataValue('slidesOrigin') ||
          'auto',
        perView:
          this.getDataValue('tabletSlidesPerView') ||
          this.getDataValue('slidesPerView') ||
          2,
        spacing:
          this.getDataValue('tabletSlidesSpacing') ||
          this.getDataValue('slidesSpacing') ||
          0,
      },
    }

    return tabletOptions
  }

  /* Desktop Options */

  get desktopBreakpoint () {
    return 1201
  }

  get desktopBreakpointOptions () {
    const desktopOptions = {
      disabled:
        this.hasAttribute('data-desktop-carousel-disabled') ||
        this.hasAttribute('data-tablet-carousel-disabled') ||
        this.hasAttribute('data-carousel-disabled') ||
        false,
      drag:
        this.hasAttribute('data-desktop-drag') ||
        this.hasAttribute('data-tablet-drag') ||
        this.hasAttribute('data-drag') ||
        true,
      initial:
        this.getDataValue('desktopInitial') ||
        this.getDataValue('tabletInitial') ||
        this.getDataValue('initial') ||
        0,
      loop:
        this.hasAttribute('data-desktop-loop') ||
        this.hasAttribute('data-tablet-loop') ||
        this.hasAttribute('data-loop') ||
        false,
      mode:
        this.getDataValue('desktopMode') ||
        this.getDataValue('tabletMode') ||
        this.getDataValue('mode') ||
        'snap',
      rubberband:
        this.hasAttribute('data-desktop-rubberband') ||
        this.hasAttribute('data-tablet-rubberband') ||
        this.hasAttribute('data-rubberband') ||
        false,
      slides: {
        origin:
          this.getDataValue('desktopSlidesOrigin') ||
          this.getDataValue('tabletSlidesOrigin') ||
          this.getDataValue('slidesOrigin') ||
          'auto',
        perView:
          this.getDataValue('desktopSlidesPerView') ||
          this.getDataValue('tabletSlidesPerView') ||
          this.getDataValue('slidesPerView') ||
          4,
        spacing:
          this.getDataValue('desktopSlidesSpacing') ||
          this.getDataValue('tabletSlidesSpacing') ||
          this.getDataValue('slidesSpacing') ||
          0,
      },
    }

    return desktopOptions
  }

  get loop () {
    return this.carousel.options.loop
  }

  setCarouselState (instance) {
    this.hasChanged = true
    this.currentIndex = instance?.track?.details?.rel || 0
  }

  /* Active Slide */

  get slideClick () {
    return this.hasAttribute('data-slide-click')
  }

  get activeSlide () {
    return this.els?.slide?.elements.filter(el => el.ariaCurrent === 'true')
  }

  setActiveSlide (instance, index) {
    const currentSlide = index != null ? index : instance.track.details.rel

    this.carousel.slides.forEach(progress => {
      progress.setAttribute(
        'aria-current',
        progress?.dataset?.slide === currentSlide.toString(),
      )
    })
  }

  setSlideListener () {
    if (!this.slideClick || !this?.els?.slides?.elements) return

    this.onSlideClick = this.onSlideClick.bind(this)

    /*
    this.carousel.slides.forEach(slide => {
      this.listeners.add(slide, 'click', this.onSlideClick)
    })
    */

    this.els.slides.elements.forEach(slide => {
      this.listeners.add(slide, 'click', this.onSlideClick)
    })
  }

  onSlideClick (e) {
    this.setActiveSlide(null, parseInt(e.currentTarget.dataset.slide))
  }

  /* Progress functionality */

  get progressLinksEls () {
    return this.els['progress-links']?.elements || []
  }

  setProgressListener () {
    if (!this.progressLinksEls) return

    this.onProgressClick = this.onProgressClick.bind(this)

    this.progressLinksEls.forEach(progress => {
      this.listeners.add(progress, 'click', this.onProgressClick)
    })
  }

  onProgressClick (e) {
    this.carousel.moveToIdx(parseInt(e.currentTarget.dataset.slide))
  }

  get currentPerView () {
    return parseInt(this.carousel.options.slides.perView, 10)
  }

  get progressBarWidth () {
    if (!this.loop) {
      return (this.currentPerView * 100) / this.carousel.slides.length
    }
    return 100 / this.carousel.slides.length
  }

  get progressEl () {
    return this.els.progress?.element
  }

  get progressPercentageEl () {
    return this.els['progress-percentage']?.element
  }

  setProgressBarWidth () {
    if (!this.progressPercentageEl) return

    this.progressPercentageEl.style.setProperty(
      '--bar-width',
      `${this.progressBarWidth}%`,
    )

    this.progressEl.classList.toggle(
      'car-Carousel_Progress-hidden',
      this.progressBarWidth >= 100,
    )
  }

  setProgressPercentage (instance = this.carousel) {
    if (!this.progressPercentageEl) return

    this.movementPercentage =
      (100 / this.slideCount) * instance.track.details.rel
    if (this.movementPercentage + this.progressBarWidth > 100) {
      this.movementPercentage = 100 - this.progressBarWidth
    }

    this.progressPercentageEl.style.setProperty(
      '--progress',
      `${this.movementPercentage}%`,
    )
  }

  setProgress (instance) {
    if (!this.progressLinksEls) return

    this.progressLinksEls.forEach(progress => {
      progress.setAttribute(
        'aria-current',
        parseInt(progress.dataset.slide) === instance.track.details.rel,
      )
    })
  }

  /* Buttons */

  get Buttons () {
    return this.els?.button?.elements || []
  }

  setButtonListener () {
    if (!this.Buttons || !this.Buttons.length) return
    this.Buttons.forEach(button => {
      this.listeners.add(button, 'click', this.onButtonClick.bind(this))
    })
  }

  onButtonClick (event) {
    const button = event.target.closest(this.els.button.selector)
    this.currentIndex =
      button.dataset.action === 'previous'
        ? this.currentIndex - 1
        : this.currentIndex + 1
    this.carousel.moveToIdx(this.currentIndex)
  }

  set currentIndex (index) {
    if (!this.loop && index <= 0) {
      index = 0
    }

    if (!this.loop && index >= this.maxIndex) {
      index = this.maxIndex
    }

    this.hasScrolled = (this.loop && this.hasChanged) || index > 0
    this.hasFinished = !this.loop && index === this.maxIndex

    this._currentIndex = index
  }

  get currentIndex () {
    return this._currentIndex
  }

  get maxIndex () {
    return Math.ceil(this.slideEls.length - this.currentPerView)
  }

  set hasScrolled (currentIndex) {
    this._hasScrolled = currentIndex > 0
    this.setAttribute('data-has-scrolled', this._hasScrolled.toString())
  }

  get hasScrolled () {
    return this._hasScrolled
  }

  set hasFinished (boolean) {
    this._hasFinished = boolean
    this.setAttribute('data-has-finished', this._hasFinished.toString())
  }

  get hasFinished () {
    return this._hasFinished
  }
}

if (!customElements.get('global-carousel')) {
  customElements.define('global-carousel', GlobalCarousel)
}
